import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Music, Play, Pause, RotateCcw, Volume2 } from "lucide-react";

const SONGS = {
  "/": "/audio/patience.mp3",
  "/search": "/audio/until_the_end.mp3",
  "/review/new": "/audio/adore.mp3",
  "/zodiac": "/audio/mine.mp3",
  "/admin/stats": "/audio/limousine.mp3",
  "/notifications": "/audio/summoning.mp3",
  "/my-story": "/audio/loneliest_girl.mp3",
  "/purpose": "/audio/i_love_me.mp3",
  "/goodbye": "/audio/leave_a_light_on.mp3",
  "/feedback": "/audio/still_into_you.mp3",
  "/report": "/audio/habits.mp3",
  "/account": "/audio/go_get_it.mp3",
};

export const PageMusic = () => {
  const { pathname } = useLocation();
  const src = pathname.startsWith("/profile/") ? "/audio/summoning.mp3" : SONGS[pathname];
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(50);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!src) { setPlaying(false); return; }
    const audio = new Audio(src);
    audio.loop = false;
    audio.volume = 0.5;
    audioRef.current = audio;
    setVolume(50);
    const onPlay = () => setPlaying(true);
    const onStop = () => setPlaying(false);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onStop);
    // When a song ends, keep the vibe going with another random track from the library
    const onEnded = () => {
      const all = [...new Set(Object.values(SONGS))];
      const current = new URL(audio.src, window.location.origin).pathname;
      const others = all.filter(s => s !== current);
      audio.src = others[Math.floor(Math.random() * others.length)] || current;
      audio.play().catch(() => setPlaying(false));
    };
    audio.addEventListener("ended", onEnded);
    let removeListeners = () => {};
    audio.play().catch(() => {
      const start = () => { audio.play().catch(() => {}); removeListeners(); };
      removeListeners = () => {
        document.removeEventListener("pointerdown", start);
        document.removeEventListener("keydown", start);
      };
      document.addEventListener("pointerdown", start);
      document.addEventListener("keydown", start);
    });
    return () => {
      removeListeners();
      audio.pause();
      audio.src = "";
      audioRef.current = null;
    };
  }, [src]);

  if (!src) return null;

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      if (a.ended) a.currentTime = 0;
      a.play().catch(() => {});
    } else a.pause();
  };
  const replay = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => {});
  };
  const changeVolume = (v) => {
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v / 100;
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 flex items-center gap-2" data-testid="page-music">
      {open && (
        <div className="flex items-center gap-2 bg-white/70 backdrop-blur-lg border border-black/10 rounded-full shadow-lg px-3 py-2">
          <button onClick={toggle} data-testid="music-toggle" aria-label={playing ? "Pause song" : "Play song"}
            className="w-9 h-9 rounded-full bg-[#FF1A82] text-white flex items-center justify-center hover:brightness-110">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </button>
          <button onClick={replay} data-testid="music-replay" aria-label="Play song again"
            className="w-9 h-9 rounded-full bg-[#10B981] text-white flex items-center justify-center hover:brightness-110">
            <RotateCcw className="w-4 h-4" />
          </button>
          <Volume2 className="w-4 h-4 text-[#666666]" />
          <input type="range" min="0" max="100" value={volume} data-testid="music-volume"
            onChange={(e) => changeVolume(Number(e.target.value))}
            className="w-20 accent-[#FF1A82]" aria-label="Volume" />
        </div>
      )}
      <button onClick={() => setOpen(o => !o)} data-testid="music-open"
        aria-label="Music controls"
        className="w-11 h-11 rounded-full bg-white/70 backdrop-blur-lg border border-black/10 shadow-lg flex items-center justify-center text-[#FF1A82] hover:text-[#10B981]">
        <Music className="w-5 h-5" />
      </button>
    </div>
  );
};
